<template>
  <section class="customer-register">
    <header><BaseTitle2 title="이용약관" /></header>
    <main>
      <i18n-t keypath="general.customer_register_desc1" tag="div" class="description1">
        <template #customer_register>
          <span>{{ t('label.customer_register') }}</span>
        </template>
      </i18n-t>
      <div class="description2">{{ t('general.customer_register_desc2') }}</div>
      <div class="description3">
        <table>
          <thead>
            <tr>
              <th>{{ t('label.collection_item') }}</th>
              <th>{{ t('label.collection_purpose') }}</th>
              <th>{{ t('label.retention_period') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ t('label.mobile_number') }}</td>
              <td>
                <ul>
                  <li>• {{ t('label.point_management') }}</li>
                  <li>• {{ t('label.service_guide') }}</li>
                  <li>• {{ t('label.event_message') }}</li>
                </ul>
              </td>
              <td>{{ t('label.until_withdrawal') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="description4">
        {{ t('general.customer_register_desc3') }}
      </div>
    </main>
    <footer>
      <div class="buttons">
        <BaseButtonGroup>
          <BaseButton
            class="outline"
            :flex="1"
            :count="autoCloseTime"
            @mouseup="handleDisagreeClick"
            @touchend.prevent="handleDisagreeClick"
          >
            {{ t('button.disagree') }}
          </BaseButton>
          <BaseButton :flex="1" @mouseup="handleAgreeClick" @touchend.prevent="handleAgreeClick">
            <template #icon>
              <img src="@/assets/images/icon-check-white.svg" alt="동의하기 아이콘" />
            </template>
            {{ t('button.agree') }}
          </BaseButton>
        </BaseButtonGroup>
      </div>
    </footer>
  </section>
</template>

<script setup lang="ts">
  import { addCustomer, getCustomer } from '@/api/server';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import OrderCloudCustomer from '@/interfaces/OrderCloudCustomer';
  import { useModalStore } from '@/stores/modal';
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BaseTitle2 from './base/BaseTitle2.vue';

  const props = defineProps<{
    /** 휴대폰 번호 */
    mobileNumber: string;
  }>();

  const { t } = useI18n();
  const modalStore = useModalStore();

  let timer: any;
  const time = 30;

  // 자동 모달 닫는 시간 (초)
  const autoCloseTime = ref(0);

  onMounted(() => {
    autoCloseTime.value = time;
    timer = setInterval(() => {
      if (--autoCloseTime.value === 0) {
        handleDisagreeClick();
      }
    }, 1000);
  });

  onBeforeUnmount(() => {
    clearInterval(timer);
  });

  /** 미동의 버튼 클릭 */
  const handleDisagreeClick = () => modalStore.resolveSyncModal<OrderCloudCustomer | null>(null);

  /** 동의하기 버튼 클릭 */
  const handleAgreeClick = async () => {
    // 회원 추가
    await addCustomer(props.mobileNumber);

    // 가입한 회원 조회
    const customer = await getCustomer(props.mobileNumber);

    // 회원가입 모달 닫기
    modalStore.resolveSyncModal<OrderCloudCustomer | null>(customer);
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .customer-register {
    display: grid;
    grid-template-rows: auto 1fr auto;
    height: 100%;
    padding: 7.0313vw;

    header {
      margin-bottom: 1.5625vw;
      @media (max-width: vars.$sm) {
        margin-bottom: 16px;
      }
    }

    main {
      overflow-y: auto;

      .description1 {
        font-size: 3.9063vw;
        font-weight: 600;
        word-break: keep-all;
        margin-bottom: 0.7813vw;

        @media (max-width: vars.$sm) {
          font-size: 22px;
          margin-bottom: 10px;
        }

        span {
          color: var(--brand-primary);
        }
      }

      .description2 {
        font-size: 2.1875vw;
        color: #6d6d6d;
        margin-bottom: 2.3438vw;
        @media (max-width: vars.$sm) {
          font-size: 14px;
          margin-bottom: 20px;
        }
      }

      .description3 {
        font-size: 1.7188vw;
        margin-bottom: 0.7813vw;
        @media (max-width: vars.$sm) {
          font-size: 14px;
          margin-bottom: 10px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          text-align: center;

          thead {
            background-color: #ececec;

            th {
              padding: 0.7813vw;
              border: 1px solid #d2d2d2;
            }
          }

          tbody {
            td {
              padding: 0.7813vw;
              border: 1px solid #d2d2d2;
              color: #808080;
              word-break: keep-all;

              ul {
                list-style: none;
                padding-left: 0;

                li {
                  text-align: left;
                }
              }
            }
          }
        }
      }

      .description4 {
        position: relative;
        padding-left: 1rem;
        font-size: 1.875vw;
        color: #6d6d6d;
        margin-bottom: 0.7813vw;
        word-break: keep-all;

        &::before {
          content: '※';
          position: absolute;
          left: 0;
        }

        @media (max-width: vars.$sm) {
          font-size: 12px;
          margin-bottom: 10px;
        }
      }
    }

    footer {
      display: flex;
      justify-content: center;
      padding-top: 1.5625vw;
      .buttons {
        width: 46.75vw;
        @media (max-width: vars.$sm) {
          width: 100%;
        }
      }
    }
  }
</style>
